import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogActions } from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { Button, buttonModes, buttonTypes } from '..'
import { Logger, LoggerTypes } from '../../utils'

/**
 * This dialog is used to display any editable forms
 * that contains a <form> element.
 * The [save] button submit this form.
 * The form container should listen to the onSubmit event to handle the request.
 * Ex: <DialogFormEdit>
 *       <MyForm/>
 *     </DialogFormEdit>
 * with :
 *
 * @returns {Dialog}
 **/
export const DialogFormEdit = ({ openDialog, setOpenDialog, children, maxWidth, className }) => {
  const { t } = useTranslation()
  const childrenRef = useRef({})

  const onCloseDialog = (e) => {
    e.preventDefault()
    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFormEdit.onCloseDialog()', { e })
    setOpenDialog(false)
  }

  const onSubmitForm = (e) => {
    e.preventDefault()
    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFormEdit.onSubmitForm()', { e, childrenRef })
    childrenRef.current.onSubmitForm()
  }

  // Logger.trace(LoggerTypes.CONTAINER, 'DialogFormEdit.render()', { childrenRef, children })

  return (
    <Dialog
      fullWidth={true}
      maxWidth={maxWidth}
      // onClose={onCloseDialog}  // <-- close while clicking on background
      open={openDialog}
      aria-labelledby="form-dialog-title"
      className={`${className}`}
    >
      <DialogContent>
        {React.cloneElement(children, {
          ref: (ref) => (childrenRef.current = ref)
        })}
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onCloseDialog}
        />
        <Button
          mode={buttonModes.PRIMARY_ACTION}
          type={buttonTypes.SUBMIT}
          text={t('common.save')}
          onClick={onSubmitForm}
          icon={<SaveOutlinedIcon />}
        />
      </DialogActions>
    </Dialog>
  )
}

DialogFormEdit.displayName = 'DialogFormEdit'

DialogFormEdit.propTypes = {
  className: PropTypes.string,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  children: PropTypes.object.isRequired,
  maxWidth: PropTypes.string
}

DialogFormEdit.defaultProps = {
  className: '',
  openDialog: false,
  setOpenDialog: () => {},
  children: null,
  maxWidth: 'md'
}
